<script setup>
// Quick sales-presentation summary for one project. Pulls hero imagery from
// the same customer-shareable documents the Media tab lists, and an
// inventory summary/starting price from the same customer-shareable units
// the Inventory tab lists — no separate source of truth, no invented data.
// Project USP / Builder Story reuse the existing Nexceo::Project
// #knowledge_base fields (usp / builder_name / builder_track_record) that
// Settings > NEXCEO Projects > [project] > Knowledge already manages — no
// new fields, no new storage. Fetched independently of hero/inventory so a
// knowledge-read hiccup never blocks the rest of the page.
import { ref, computed, onMounted } from 'vue';
import projectDocumentsAPI from 'dashboard/api/nexceo/projectDocuments';
import unitInventoryAPI from 'dashboard/api/nexceo/unitInventory';
import projectKnowledgeAPI from 'dashboard/api/nexceo/projectKnowledge';

const props = defineProps({
  project: { type: Object, required: true },
  projectId: { type: Number, required: true },
});

const heroImageUrl = ref(null);
const units = ref([]);
const isLoadingExtras = ref(true);
const knowledge = ref({});

const STATUS_META = {
  under_construction: { label: 'Under Construction', emoji: '🟡' },
  ready_to_move: { label: 'Ready to Move', emoji: '🟢' },
  sold: { label: 'Sold', emoji: '⚫' },
};

const statusLabel = computed(
  () => STATUS_META[props.project.status]?.label ?? props.project.status
);

const locationLabel = computed(() =>
  [props.project.location, props.project.city, props.project.state]
    .filter(Boolean)
    .join(', ')
);

const customerUnits = computed(() =>
  units.value.filter(u => u.visibility === 'customer_shareable')
);

const inventorySummary = computed(() => {
  const total = customerUnits.value.length;
  const available = customerUnits.value.filter(
    u => u.status === 'available'
  ).length;
  return { total, available };
});

function formatMoney(v) {
  const n = Number(v);
  if (!n) return null;
  const sym =
    props.project.currency === 'INR' ? '₹' : props.project.currency || '';
  if (n >= 10000000) return `${sym}${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `${sym}${(n / 100000).toFixed(2)}L`;
  return `${sym}${n.toLocaleString()}`;
}

const startingPrice = computed(() => {
  const pricedAvailable = customerUnits.value
    .filter(u => u.status === 'available' && u.price)
    .map(u => Number(u.price));
  if (pricedAvailable.length) {
    return formatMoney(Math.min(...pricedAvailable));
  }
  if (props.project.price_min) return formatMoney(props.project.price_min);
  return null;
});

async function loadExtras() {
  isLoadingExtras.value = true;
  try {
    const [{ data: docs }, { data: unitList }] = await Promise.all([
      projectDocumentsAPI.get(props.projectId, { status: 'active' }),
      unitInventoryAPI.list(props.projectId),
    ]);
    const hero = (Array.isArray(docs) ? docs : []).find(
      d => d.document_type === 'image' && d.customer_shareable
    );
    heroImageUrl.value = hero?.shareable_url ?? null;
    units.value = Array.isArray(unitList) ? unitList : [];
  } catch {
    // Non-fatal — Overview still renders the core project fields below.
  } finally {
    isLoadingExtras.value = false;
  }
}

async function loadKnowledge() {
  try {
    const { data } = await projectKnowledgeAPI.show(props.projectId);
    knowledge.value = data || {};
  } catch {
    // Non-fatal — USP/Builder Story sections just stay hidden.
  }
}

onMounted(() => {
  loadExtras();
  loadKnowledge();
});

// Amenities are plain strings (project.amenities) — no icon field in the
// data model. This is a decorative, keyword-based lookup over that existing
// text, not a new data source; unrecognized amenities fall back to a
// generic icon rather than guessing at meaning.
const AMENITY_ICON_RULES = [
  [/pool/i, 'i-lucide-waves'],
  [/gym|fitness/i, 'i-lucide-dumbbell'],
  [/club\s*house/i, 'i-lucide-building-2'],
  [/garden|landscape/i, 'i-lucide-flower-2'],
  [/park(?!ing)/i, 'i-lucide-trees'],
  [/parking/i, 'i-lucide-car'],
  [/power\s*back\s*up|generator/i, 'i-lucide-zap'],
  [/security|guard|cctv|camera/i, 'i-lucide-shield-check'],
  [/kids?\s*play|play\s*area|playground/i, 'i-lucide-baby'],
  [/lift|elevator/i, 'i-lucide-move-vertical'],
  [/gate|entrance/i, 'i-lucide-door-open'],
  [/yoga|meditation/i, 'i-lucide-flower'],
  [/jog|walk(ing)?\s*track/i, 'i-lucide-footprints'],
  [/water/i, 'i-lucide-droplets'],
  [/wifi|internet/i, 'i-lucide-wifi'],
  [/library|reading/i, 'i-lucide-book-open'],
  [/indoor\s*games|table\s*tennis|badminton|sports|court/i, 'i-lucide-trophy'],
  [/community\s*hall|banquet|hall/i, 'i-lucide-users'],
  [/fire/i, 'i-lucide-flame'],
  [/rain\s*water/i, 'i-lucide-cloud-rain'],
  [/solar/i, 'i-lucide-sun'],
];
const DEFAULT_AMENITY_ICON = 'i-lucide-sparkles';
function amenityIcon(name) {
  const rule = AMENITY_ICON_RULES.find(([pattern]) => pattern.test(name));
  return rule ? rule[1] : DEFAULT_AMENITY_ICON;
}

// Project USP is one free-text field (knowledge_base.usp), but the real
// admin-entered content is already bullet-shaped — one "Title – description"
// pair per line, with an optional closing quote/tagline line. Parsed here
// for a structured card layout rather than duplicated as a second field
// elsewhere; nothing is dropped, every non-blank line ends up in either
// uspItems or uspTagline.
const uspLines = computed(() =>
  (knowledge.value.usp || '')
    .split('\n')
    .map(line => line.trim())
    .filter(line => line && !/^[.\-–—]+$/.test(line))
);
const uspTagline = computed(
  () => uspLines.value.find(line => /^[“"]/.test(line)) || null
);
const uspItems = computed(() =>
  uspLines.value
    .filter(line => line !== uspTagline.value)
    .map(line => {
      const match = line.match(/^(.+?)\s[–—]\s(.+)$/);
      return match
        ? { title: match[1].trim(), description: match[2].trim() }
        : { title: line, description: '' };
    })
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex-1 overflow-y-auto">
    <!-- Hero -->
    <div
      class="h-56 w-full flex items-center justify-center relative overflow-hidden"
      :class="
        heroImageUrl
          ? ''
          : 'bg-gradient-to-br from-woot-500/20 via-woot-400/10 to-transparent'
      "
    >
      <img
        v-if="heroImageUrl"
        :src="heroImageUrl"
        alt=""
        class="absolute inset-0 size-full object-cover"
      />
      <span
        v-if="!heroImageUrl"
        class="i-lucide-building-2 size-16 text-woot-500/50"
      />
      <div
        class="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/60 to-transparent"
        aria-hidden="true"
      />
      <div
        class="absolute bottom-4 left-6 right-6 flex items-end justify-between gap-3"
      >
        <div>
          <h1 class="text-2xl font-bold text-white drop-shadow-sm">
            {{ project.name }}
          </h1>
          <p
            v-if="locationLabel"
            class="flex items-center gap-1 text-sm text-white/90 mt-1"
          >
            <span class="i-lucide-map-pin size-3.5" />
            {{ locationLabel }}
          </p>
        </div>
        <span
          class="shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full bg-white/90 text-n-slate-12"
        >
          {{ STATUS_META[project.status]?.emoji }} {{ statusLabel }}
        </span>
      </div>
    </div>

    <div class="p-6 flex flex-col gap-6 max-w-4xl mx-auto">
      <!-- Key facts -->
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div class="rounded-xl border border-n-weak p-3">
          <p class="text-xs text-n-slate-8">Starting Price</p>
          <p class="text-sm font-semibold text-n-slate-12 mt-0.5">
            {{ startingPrice ? `${startingPrice} onwards` : '—' }}
          </p>
        </div>
        <div class="rounded-xl border border-n-weak p-3">
          <p class="text-xs text-n-slate-8">Property Type</p>
          <p class="text-sm font-semibold text-n-slate-12 mt-0.5">
            {{ project.property_type || '—' }}
          </p>
        </div>
        <div class="rounded-xl border border-n-weak p-3">
          <p class="text-xs text-n-slate-8">Delivery</p>
          <p class="text-sm font-semibold text-n-slate-12 mt-0.5">
            {{ project.delivery_year || '—' }}
          </p>
        </div>
        <div class="rounded-xl border border-n-weak p-3">
          <p class="text-xs text-n-slate-8">Availability</p>
          <p class="text-sm font-semibold text-n-slate-12 mt-0.5">
            <template v-if="!isLoadingExtras">
              {{ inventorySummary.available }} /
              {{ inventorySummary.total }} units
            </template>
            <span
              v-else
              class="i-lucide-loader-2 size-3.5 animate-spin inline-block"
            />
          </p>
        </div>
      </div>

      <!-- Description -->
      <p
        v-if="project.description"
        class="text-sm text-n-slate-11 leading-relaxed"
      >
        {{ project.description }}
      </p>

      <!-- Amenities -->
      <div v-if="project.amenities?.length" class="flex flex-col gap-3">
        <h2 class="text-sm font-semibold text-n-slate-12">Amenities</h2>
        <div class="grid gap-3 grid-cols-[repeat(auto-fill,minmax(84px,1fr))]">
          <div
            v-for="amenity in project.amenities"
            :key="amenity"
            class="flex flex-col items-center gap-2 text-center p-3 rounded-xl border border-n-weak"
          >
            <span
              class="flex items-center justify-center size-10 rounded-full bg-woot-50 dark:bg-woot-500/10 shrink-0"
            >
              <span
                class="size-5 text-woot-600 dark:text-woot-400"
                :class="amenityIcon(amenity)"
              />
            </span>
            <span class="text-xs font-medium text-n-slate-11 leading-snug">
              {{ amenity }}
            </span>
          </div>
        </div>
      </div>

      <!-- Project USP -->
      <div v-if="uspItems.length" class="flex flex-col gap-3">
        <h2
          class="flex items-center gap-1.5 text-sm font-semibold text-n-slate-12"
        >
          <span class="i-lucide-sparkles size-4 text-woot-500" />
          Project USP
        </h2>
        <div class="grid gap-x-6 gap-y-4 sm:grid-cols-2">
          <div
            v-for="(item, i) in uspItems"
            :key="i"
            class="flex items-start gap-2.5"
          >
            <span
              class="i-lucide-check size-4 text-green-600 dark:text-green-400 mt-0.5 shrink-0"
            />
            <div>
              <p class="text-sm font-semibold text-n-slate-12">
                {{ item.title }}
              </p>
              <p
                v-if="item.description"
                class="text-xs text-n-slate-9 mt-0.5 leading-relaxed"
              >
                {{ item.description }}
              </p>
            </div>
          </div>
        </div>
        <p
          v-if="uspTagline"
          class="text-sm italic text-n-slate-10 border-l-2 border-woot-300 dark:border-woot-700 pl-3 mt-1"
        >
          {{ uspTagline }}
        </p>
      </div>

      <!-- Builder Story -->
      <div
        v-if="knowledge.builder_name || knowledge.builder_track_record"
        class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
      >
        <div class="flex items-center gap-2">
          <span
            class="flex items-center justify-center size-9 rounded-full bg-woot-50 dark:bg-woot-500/10 shrink-0"
          >
            <span
              class="i-lucide-building size-4 text-woot-600 dark:text-woot-400"
            />
          </span>
          <div>
            <p class="text-xs text-n-slate-8">Builder Story</p>
            <p
              v-if="knowledge.builder_name"
              class="text-sm font-semibold text-n-slate-12"
            >
              {{ knowledge.builder_name }}
            </p>
          </div>
        </div>
        <p
          v-if="knowledge.builder_track_record"
          class="text-sm text-n-slate-11 leading-relaxed whitespace-pre-line"
        >
          {{ knowledge.builder_track_record }}
        </p>
      </div>
    </div>
  </div>
</template>
